import { useQuery, useQueryClient } from 'react-query';
import { addDays, addMonths, format, subMonths } from 'date-fns';
import { getCalendarEvents } from 'api/orders/api';
import { useMemo } from 'react';

export const useCalendarData = (trackingDate: Date, selectedDate: Date) => {
  const queryClient = useQueryClient();

  const calendarMonth = trackingDate.getMonth() + 1;
  const calendarYear = trackingDate.getFullYear();

  const calendarQuery = useQuery(
    ['calendar-events', calendarMonth, calendarYear],
    () => getCalendarEvents({ month: calendarMonth, year: calendarYear }),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      onSuccess: () => {
        const next = addMonths(trackingDate, 1);
        const prev = subMonths(trackingDate, 1);

        const prefetchMonth = (date: Date) => {
          const m = date.getMonth() + 1;
          const y = date.getFullYear();
          queryClient.prefetchQuery(
            ['calendar-events', m, y],
            () => getCalendarEvents({ month: m, year: y }),
            { staleTime: 5 * 60 * 1000 },
          );
        };

        prefetchMonth(next);
        prefetchMonth(prev);
      },
    },
  );

  const { fromDate, toDate } = useMemo(() => {
    const from = selectedDate;
    const to = addDays(selectedDate, 13);
    return {
      fromDate: format(from, 'yyyy-MM-dd'),
      toDate: format(to, 'yyyy-MM-dd'),
    };
  }, [selectedDate]);

  const eventListQuery = useQuery(
    ['event-list-events', fromDate, toDate],
    () => getCalendarEvents({ from_date: fromDate, to_date: toDate }),
    {
      enabled: !!selectedDate,
      staleTime: 2 * 60 * 1000,
      refetchOnWindowFocus: true,
    },
  );

  return {
    calendarEvents: calendarQuery.data,
    eventListEvents: eventListQuery.data,
    isEventListLoading: eventListQuery.isLoading,
  };
};
